import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { formatInTz } from '../lib/utcClock.js';
import { DEFAULT_TIMEZONE } from '../lib/timezones.js';

function formatMtime(ms, tz) {
  if (!ms) return '—';
  return formatInTz(ms, tz);
}

function pick(m, key) {
  return m && m[key] != null && m[key] !== '' ? m[key] : null;
}

// Prefer metadata.osm_date, then sidecar/date, then planetiler OSM replication timestamp.
function dataDate(m) {
  return pick(m, 'osm_date')
      ?? pick(m, 'date')
      ?? pick(m, 'created')
      ?? pick(m, 'planetiler:osm:osmosisreplicationtime')
      ?? null;
}

function sizeLabel(m) {
  const gb = pick(m, 'total_size_gb') ?? pick(m, 'estimated_size_gb');
  return gb ? `${gb} GB` : null;
}

function zoomRange(m) {
  const min = pick(m, 'minzoom');
  const max = pick(m, 'maxzoom');
  if (min == null && max == null) return null;
  return `${min ?? '?'}–${max ?? '?'}`;
}

function DatasetCard({ d, tz }) {
  const m = d.metadata;
  const title = pick(m, 'name') ?? d.name;

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
        <span className="rounded bg-ink-100 px-2 py-0.5 font-mono text-xs text-ink-700">{d.name}</span>
        {pick(m, 'type') && <span className="rounded bg-ink-100 px-2 py-0.5 text-xs text-ink-600">{m.type}</span>}
        {d.available
          ? <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">available</span>
          : <span className="rounded bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">missing</span>}
      </div>

      {pick(m, 'description') && (
        <p className="mt-2 text-xs text-ink-600">{m.description}</p>
      )}

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
        <Row label="Path"><span className="font-mono break-all">{d.path || '—'}</span></Row>
        <Row label="Format">{d.format || pick(m, 'compression') || '—'}</Row>
        <Row label="Encoding">{d.encoding || '—'}</Row>
        <Row label="Zoom">{zoomRange(m) || (d.max_zoom != null ? `0–${d.max_zoom}` : '—')}</Row>
        <Row label="Max zoom (active)">{d.max_zoom ?? 'auto'}</Row>
        <Row label="Data date">{dataDate(m) || '—'}</Row>
        <Row label="Build">{pick(m, 'planetiler:buildtime') || '—'}</Row>
        <Row label="Version">{pick(m, 'version') || '—'}</Row>
        <Row label="Resolution">{pick(m, 'resolution') || '—'}</Row>
        <Row label="Size">{sizeLabel(m) || '—'}</Row>
        <Row label="Files">{pick(m, 'file_count') ? pick(m, 'file_count').toLocaleString() : '—'}</Row>
        <Row label="Dir modified">{formatMtime(d.mtime, tz)}</Row>
      </div>

      {pick(m, 'attribution') && (
        <div className="mt-3 border-t border-ink-100 pt-2 text-[11px] text-ink-500"
             dangerouslySetInnerHTML={{ __html: m.attribution }} />
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-400">{label}</div>
      <div className="text-ink-800">{children}</div>
    </div>
  );
}

export default function Datasets() {
  const { user } = useAuth();
  const tz = user?.timezone || DEFAULT_TIMEZONE;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/datasets')
      .then(setData)
      .catch((err) => setError(err?.data?.error ?? err.message ?? 'Failed to load'));
  }, []);

  if (error) {
    return (
      <div className="card p-6">
        <h2 className="mb-2 text-sm font-semibold text-ink-700">Datasets</h2>
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card p-6">
        <h2 className="mb-2 text-sm font-semibold text-ink-700">Datasets</h2>
        <p className="text-sm text-ink-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h2 className="text-sm font-semibold text-ink-700">Datasets</h2>
        <p className="mt-1 text-xs text-ink-500">
          Read-only. Source of truth: <code>.env</code> (<code>DATASETS</code>, <code>&lt;NAME&gt;_TILES_PATH</code>) +
          <code> metadata.json</code> on disk. Restart the server after changing <code>.env</code>.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {data.datasets.map((d) => <DatasetCard key={d.name} d={d} tz={tz} />)}
      </div>

      <ElevationPanel tz={tz} />

      <div className="card p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">Service endpoints</h3>
        <div className="flex flex-wrap gap-2">
          {data.services.map((s) => (
            <span key={s} className="rounded-lg border border-ink-200 px-3 py-1 font-mono text-xs text-ink-700">{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Admin panel for the elevation service. Reads /api/elevation/info on mount
// and exposes a Rescan button that hits /api/elevation/refresh. `lastRefresh`
// is session-local — we don't persist it server-side since the only consumer
// is this view.
function ElevationPanel({ tz }) {
  const [maxZoom, setMaxZoom] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  useEffect(() => {
    let alive = true;
    api.get('/elevation/info')
      .then((r) => { if (alive) setMaxZoom(r.max_zoom); })
      .catch((err) => { if (alive) setError(err?.data?.error ?? err.message ?? 'Failed to load'); });
    return () => { alive = false; };
  }, []);

  async function rescan() {
    if (!confirm('Rescan elevation tile directory to refresh max_zoom? Safe to run anytime.')) return;
    setBusy(true);
    setError('');
    setFlash('');
    try {
      const r = await api.post('/elevation/refresh');
      setMaxZoom(r.max_zoom);
      setLastRefresh(new Date());
      setFlash(`Rescanned. Max zoom = ${r.max_zoom}.`);
    } catch (err) {
      setError(err?.data?.error ?? err.message ?? 'Rescan failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-500">Elevation service</h3>
          <p className="mt-1 text-xs text-ink-500">
            Point-elevation lookup (<code>GET /elevation?lat=&amp;lon=</code>) reads from the terrain
            RGB tile source. Rescan after adding new zoom levels on disk.
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={rescan}
          disabled={busy}
          title="Rescan tile directory"
        >
          {busy
            ? <><i className="fa-solid fa-spinner fa-spin-pulse" /> Rescanning…</>
            : <><i className="fa-solid fa-arrows-rotate" /> Rescan tiles</>}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
        <Row label="Max zoom (live)">
          {maxZoom == null ? <span className="text-ink-400">—</span> : <span className="font-mono">{maxZoom}</span>}
        </Row>
        <Row label="Source">terrain (RGB)</Row>
        <Row label="Lookup URL"><span className="font-mono">/elevation</span></Row>
        <Row label="Last refresh">
          {lastRefresh ? <span className="font-mono">{formatInTz(lastRefresh, tz)}</span> : <span className="text-ink-400">—</span>}
        </Row>
      </div>

      {flash && (
        <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{flash}</div>
      )}
      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
      )}
    </div>
  );
}
